import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import Icon from '../Icon';

export default function WebSocketsInfographic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [pulseCount, setPulseCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      const interval = setInterval(() => {
        setPulseCount(c => (c + 1) % 4);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isInView]);

  return (
    <div ref={ref} className="relative w-full">
      {/* Friendly explanation */}
      <div className="mb-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="bolt" className="text-2xl text-blue-600" /> What are WebSockets?
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Normally, your browser has to <strong>keep asking</strong> "any updates? any updates?" 
          That's slow and wasteful. WebSockets create a <strong>permanent phone line</strong>. The 
          server can call <em>you</em> instantly when something happens!
        </p>
        <ul className="mt-3 space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>New bid? Everyone watching <strong>sees it instantly</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>No refreshing the page, no "is my bid in yet?"</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span><strong>Hibernatable</strong>: connection stays open even when idle (saves money!)</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Updates arrive in <strong>under 50 milliseconds</strong></span>
          </li>
        </ul>
      </div>

      {/* Animated Infographic - Before/After */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* OLD WAY */}
        <div className="bg-white rounded-2xl border border-red-200 p-6">
          <h4 className="text-sm font-semibold text-red-600 mb-4 flex items-center gap-2">
            <Icon name="cancel" size="small" className="text-red-600" /> The Old Way: Polling
          </h4>
          <svg viewBox="0 0 200 180" className="w-full h-auto">
            <rect x="0" y="0" width="200" height="180" fill="#fef2f2" rx="8" />
            
            {/* Browser */}
            <rect x="20" y="30" width="60" height="50" rx="6" fill="#fee2e2" stroke="#fca5a5" strokeWidth="1" />
            <text x="50" y="58" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#991b1b">CLIENT</text>

            {/* Server */}
            <rect x="120" y="30" width="60" height="50" rx="6" fill="#fee2e2" stroke="#fca5a5" strokeWidth="1" />
            <text x="150" y="58" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#991b1b">SERVER</text>

            {/* Repeated arrows - polling */}
            {[0, 1, 2].map((i) => (
              <g key={i}>
                <motion.path
                  d={`M 85 ${45 + i * 5} L 115 ${45 + i * 5}`}
                  stroke="#dc2626"
                  strokeWidth="1"
                  strokeDasharray="3 2"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, delay: i * 0.3, repeat: Infinity }}
                />
              </g>
            ))}

            {/* Polling text */}
            <text x="100" y="100" textAnchor="middle" fontSize="8" fill="#dc2626">
              "Any updates?" × 1000/sec
            </text>

            {/* Waste indicators */}
            <motion.g
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <text x="100" y="124" textAnchor="middle" fontSize="9" fill="#991b1b" fontWeight="bold">SLOW</text>
            </motion.g>
            
            <text x="100" y="145" textAnchor="middle" fontSize="8" fill="#991b1b" fontWeight="500">
              Wasteful & Slow
            </text>
            <text x="100" y="158" textAnchor="middle" fontSize="7" fill="#6b7280">
              Constant requests, 1-sec delays
            </text>
          </svg>
        </div>

        {/* NEW WAY */}
        <div className="bg-white rounded-2xl border border-green-200 p-6">
          <h4 className="text-sm font-semibold text-green-600 mb-4 flex items-center gap-2">
            <Icon name="check_circle" size="small" className="text-green-600" /> The New Way: WebSockets
          </h4>
          <svg viewBox="0 0 200 180" className="w-full h-auto">
            <rect x="0" y="0" width="200" height="180" fill="#f0fdf4" rx="8" />
            
            {/* Browser */}
            <rect x="20" y="30" width="60" height="50" rx="6" fill="#dcfce7" stroke="#86efac" strokeWidth="1" />
            <text x="50" y="58" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#166534">CLIENT</text>

            {/* Server */}
            <rect x="120" y="30" width="60" height="50" rx="6" fill="#dcfce7" stroke="#86efac" strokeWidth="1" />
            <text x="150" y="58" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#166534">SERVER</text>

            {/* Permanent connection line */}
            <motion.line
              x1="85"
              y1="55"
              x2="115"
              y2="55"
              stroke="#22c55e"
              strokeWidth="3"
              initial={{ pathLength: 0 }}
              animate={isInView ? { pathLength: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
            />

            {/* Phone icon on line */}
            <motion.g
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 1 }}
            >
              <circle cx="100" cy="55" r="10" fill="white" stroke="#22c55e" strokeWidth="2" />
              <text x="100" y="59" textAnchor="middle" fontSize="8" fill="#22c55e" fontWeight="bold">WS</text>
            </motion.g>

            {/* Push notification */}
            <motion.g
              animate={{ x: pulseCount === 2 ? [0, -30] : 0, opacity: pulseCount === 2 ? [0, 1] : 0 }}
              transition={{ duration: 0.5 }}
            >
              <rect x="115" y="35" width="35" height="20" rx="4" fill="#22c55e" />
              <text x="132" y="48" textAnchor="middle" fontSize="7" fill="white">New bid!</text>
            </motion.g>

            <text x="100" y="100" textAnchor="middle" fontSize="8" fill="#166534">
              Server pushes updates instantly
            </text>

            <motion.g
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <text x="100" y="124" textAnchor="middle" fontSize="9" fill="#166534" fontWeight="bold">FAST</text>
            </motion.g>
            
            <text x="100" y="145" textAnchor="middle" fontSize="8" fill="#166534" fontWeight="500">
              Efficient & Instant
            </text>
            <text x="100" y="158" textAnchor="middle" fontSize="7" fill="#6b7280">
              One connection, 50ms updates
            </text>
          </svg>
        </div>
      </div>

      {/* Hibernation explanation */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h4 className="text-sm font-semibold text-gray-500 mb-4 flex items-center gap-1"><Icon name="bedtime" size="small" /> Hibernation: The Money-Saver</h4>
        <svg viewBox="0 0 500 120" className="w-full h-auto">
          <rect x="0" y="0" width="500" height="120" fill="#fafafa" rx="8" />
          
          {/* Active state */}
          <motion.g
            animate={{ opacity: pulseCount < 2 ? 1 : 0.3 }}
          >
            <rect x="30" y="20" width="130" height="80" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
            <text x="95" y="50" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1d4ed8">RUNNING</text>
            <text x="95" y="70" textAnchor="middle" fontSize="10" fill="#1d4ed8" fontWeight="500">Active</text>
            <text x="95" y="85" textAnchor="middle" fontSize="8" fill="#6b7280">Processing bids</text>
          </motion.g>

          {/* Arrow */}
          <text x="175" y="60" textAnchor="middle" fontSize="16">→</text>

          {/* Sleeping state */}
          <motion.g
            animate={{ opacity: pulseCount >= 2 ? 1 : 0.3 }}
          >
            <rect x="195" y="20" width="130" height="80" rx="8" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" />
            <text x="260" y="50" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#7c3aed">SLEEPING</text>
            <text x="260" y="70" textAnchor="middle" fontSize="10" fill="#7c3aed" fontWeight="500">Hibernating</text>
            <text x="260" y="85" textAnchor="middle" fontSize="8" fill="#6b7280">Connections kept, no CPU</text>
          </motion.g>

          {/* Arrow */}
          <text x="340" y="60" textAnchor="middle" fontSize="16">→</text>

          {/* Wake up */}
          <motion.g>
            <rect x="360" y="20" width="130" height="80" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
            <motion.text 
              x="425" 
              y="50" 
              textAnchor="middle" 
              fontSize="10"
              fontWeight="bold"
              fill="#166534"
              animate={{ scale: pulseCount === 2 ? [1, 1.3, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              WAKE
            </motion.text>
            <text x="425" y="70" textAnchor="middle" fontSize="10" fill="#166534" fontWeight="500">Wake on Message</text>
            <text x="425" y="85" textAnchor="middle" fontSize="8" fill="#6b7280">Instant response</text>
          </motion.g>
        </svg>
        <p className="text-sm text-gray-600 text-center mt-3">
          Actor sleeps when idle, wakes instantly when a message arrives. <strong>No wasted resources!</strong>
        </p>
      </div>

      {/* Real-world analogy */}
      <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
        <div className="flex items-start gap-3">
          <Icon name="smartphone" className="text-2xl text-blue-600" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Think of WhatsApp vs Email</h4>
            <p className="text-sm text-gray-600">
              <strong>Polling</strong> is like checking your email every 5 seconds: exhausting and slow.
              <strong> WebSockets</strong> are like WhatsApp. Your phone is always connected, 
              and messages <strong>appear the moment they're sent</strong>. Same for bids!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
